"use client";

import clsx from "clsx";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRouter} from "@bprogress/next";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import type {MessageContentNavigation} from "@/services/wingman";
import {buildScriptNavigationHref} from "@/utils/chat/scriptNavigation";
import {notifyScriptListRefresh} from "@/utils/scripts/scriptListRefresh";
import Icon from "../library/Icon";

const handledNavigations = new Set<string>();

function navigationGuardKey(callId: string): string {
   return `navigation-${callId}`;
}

function hasNavigated(callId: string): boolean {
   if (handledNavigations.has(callId)) return true;
   try {
      return (
         typeof window !== "undefined" &&
         window.sessionStorage.getItem(navigationGuardKey(callId)) === "1"
      );
   } catch {
      return false;
   }
}

const AskNavigationBubble: React.FC<{
   navigation: MessageContentNavigation;
   isUser?: boolean;
   className?: string;
   autoNavigate?: boolean;
   /** Conversation to keep open beside the page this navigation lands on. */
   conversationId?: string;
}> = ({navigation, isUser, className, autoNavigate = false, conversationId}) => {
   const {t} = useTranslation("ask");
   const router = useRouter();
   const {isGailGptDockEnabled, showConversationOnRight} = useGailDock();
   const startedRef = useRef(false);

   const {callId, target} = navigation;
   const href = buildScriptNavigationHref(target);

   const [fired, setFired] = useState(() => hasNavigated(callId));
   const [failed, setFailed] = useState(false);

   useEffect(() => {
      if (!autoNavigate || isUser || !href || !isGailGptDockEnabled) return;
      if (startedRef.current || hasNavigated(callId)) return;
      startedRef.current = true;
      handledNavigations.add(callId);
      try {
         window.sessionStorage.setItem(navigationGuardKey(callId), "1");
      } catch {
         // noop
      }
      try {
         notifyScriptListRefresh({
            scriptId: target.scriptId ?? undefined,
            direction: target.direction === "outbound" ? "outbound" : "inbound",
         });
         if (conversationId) showConversationOnRight(conversationId);
         router.push(href);
         setFired(true);
      } catch {
         setFailed(true);
      }
   }, [
      autoNavigate,
      isUser,
      callId,
      href,
      router,
      isGailGptDockEnabled,
      showConversationOnRight,
      conversationId,
      target.scriptId,
      target.direction,
   ]);
   if (!isGailGptDockEnabled) return null;

   const isError = !href || failed;
   const isDone = !isError && fired;

   let label: string;
   if (isError) {
      label = t("workspace.navigate.error", {defaultValue: "Couldn't navigate there"});
   } else if (isDone) {
      label = target.scriptId
         ? t("workspace.navigate.openedScript", {defaultValue: "Opened script"})
         : t("workspace.navigate.openedList", {defaultValue: "Opened scripts"});
   } else {
      label = t("workspace.navigate.navigating", {defaultValue: "Navigating…"});
   }

   return (
      <div className={clsx("flex items-center gap-1.5 py-0.5 font-source-serif", className)}>
         <Icon
            icon={isError ? "triangle-exclamation" : "arrow-right"}
            className={clsx(
               "flex-shrink-0 w-3.5 h-3.5",
               isError
                  ? "text-red-600"
                  : isDone
                    ? "text-400"
                    : "text-500 animate-text-shimmer"
            )}
         />
         <span
            className={clsx(
               "text-xs",
               isError
                  ? "text-red-600"
                  : isDone
                    ? "text-400 font-normal"
                    : "font-medium text-500 animate-text-shimmer"
            )}
         >
            {label}
         </span>
      </div>
   );
};

export default AskNavigationBubble;
